"use client";

import { SlidersHorizontal } from "lucide-react";
import { useTranslations } from "next-intl";
import type { CueSegmentationConfig } from "subtitletools";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OptionRadioGroup } from "../_components/OptionRadioGroup";
import type { SegmentationMode } from "./segmentation";

function parseStopCharacters(value: string): string[] {
  return Array.from(new Set(Array.from(value).filter((char) => char.trim().length > 0)));
}

function NumberField({
  id,
  label,
  value,
  min,
  step,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  step: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-foreground/70 text-xs">
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        min={min}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value ? Number(event.target.value) : 0)}
        className="h-8"
      />
    </div>
  );
}

export function SegmentationControl({
  mode,
  rules,
  disabled,
  estimatedBoundaries,
  onModeChange,
  onRulesChange,
  onApply,
}: {
  mode: SegmentationMode;
  rules: CueSegmentationConfig;
  disabled?: boolean;
  estimatedBoundaries: boolean;
  onModeChange: (mode: SegmentationMode) => void;
  onRulesChange: (rules: CueSegmentationConfig) => void;
  onApply: () => void;
}) {
  const t = useTranslations("mediaTools.editor");
  const patchRules = (patch: Partial<CueSegmentationConfig>) => {
    onRulesChange({ ...rules, ...patch });
  };
  const modeOptions: { value: SegmentationMode; label: string; hint?: string }[] = [
    {
      value: "original",
      label: t("segmentation.modes.original"),
      hint: t("segmentation.loadedSegments"),
    },
    { value: "one-word", label: t("segmentation.modes.oneWord") },
    { value: "custom", label: t("segmentation.modes.custom") },
  ];

  return (
    <div className="space-y-3 border-t pt-3">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-sm">{t("segmentation.title")}</p>
        <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={onApply}>
          <SlidersHorizontal className="mr-1.5 h-4 w-4" />
          {t("segmentation.apply")}
        </Button>
      </div>

      <OptionRadioGroup
        name="segmentation-mode"
        label={t("segmentation.mode")}
        value={mode}
        options={modeOptions}
        disabled={disabled}
        onChange={onModeChange}
      />

      {mode === "custom" && (
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2 space-y-1">
            <Label htmlFor="segmentation-stop-characters" className="text-foreground/70 text-xs">
              {t("segmentation.stopCharacters")}
            </Label>
            <Input
              id="segmentation-stop-characters"
              value={rules.stopCharacters.join("")}
              disabled={disabled}
              onChange={(event) =>
                patchRules({ stopCharacters: parseStopCharacters(event.target.value) })
              }
              className="h-8"
            />
          </div>
          <NumberField
            id="segmentation-max-chars"
            label={t("segmentation.maxChars")}
            value={rules.maxChars}
            min={0}
            step={1}
            disabled={disabled}
            onChange={(maxChars) => patchRules({ maxChars })}
          />
          <NumberField
            id="segmentation-max-duration"
            label={t("segmentation.maxDuration")}
            value={rules.maxDurationS}
            min={0}
            step={0.1}
            disabled={disabled}
            onChange={(maxDurationS) => patchRules({ maxDurationS })}
          />
          <NumberField
            id="segmentation-split-after-gap"
            label={t("segmentation.splitAfterGap")}
            value={rules.splitAfterGapS}
            min={0}
            step={0.1}
            disabled={disabled}
            onChange={(splitAfterGapS) => patchRules({ splitAfterGapS })}
          />
          <div className="col-span-2 flex items-center gap-2">
            <Checkbox
              id="segmentation-split-on-spaces"
              checked={rules.splitOnWhitespace}
              disabled={disabled}
              onCheckedChange={(checked) => patchRules({ splitOnWhitespace: checked === true })}
            />
            <Label htmlFor="segmentation-split-on-spaces" className="text-foreground/70 text-xs">
              {t("segmentation.splitOnSpaces")}
            </Label>
          </div>
        </div>
      )}

      {estimatedBoundaries && (
        <p className="text-foreground/40 text-xs">{t("segmentation.estimatedBoundaries")}</p>
      )}
    </div>
  );
}
